<template>
  <div class="container">
    <h2>승인 대기 리스트</h2>  

    <ag-grid-vue
      class="ag-theme-alpine grid-container"
      :rowData="pendingLeaves"
      :columnDefs="columnDefs"
      :rowSelection="'single'"
      @rowClicked="fetchLeaveDetails"
    >
    </ag-grid-vue>

    <!-- 상세 정보 다이얼로그 -->
    <el-dialog v-model="detailsDialogVisible" title="휴가 상세 정보" :width="dialogWidth" class="responsive-dialog approval-dialog">

      <!-- ★ 요청 내용 — 결재자가 가장 먼저 봐야 할 정보, 카드로 강조 -->
      <el-card v-if="leaveRequest" class="leave-request-card" shadow="hover" style="margin-bottom:16px; border-left:4px solid #409EFF;">
        <template #header>
          <div style="display:flex; align-items:center; gap:8px;">
            <span style="font-weight:700; font-size:16px;">📝 결재 요청 내용</span>
            <el-tag v-if="leaveRequest.leave_type_name === '생일반차'" type="warning" effect="dark" size="small">🎂 생일반차</el-tag>
            <el-tag v-else type="primary" effect="light" size="small">{{ leaveRequest.leave_type_name }}</el-tag>
          </div>
        </template>
        <el-row :gutter="12">
          <el-col :xs="12" :md="6"><div class="kv"><span class="k">시작일</span><span class="v">{{ leaveRequest.start_date }}</span></div></el-col>
          <el-col :xs="12" :md="6"><div class="kv"><span class="k">종료일</span><span class="v">{{ leaveRequest.end_date }}</span></div></el-col>
          <el-col :xs="12" :md="4"><div class="kv"><span class="k">일수</span><span class="v" style="color:#409EFF; font-weight:700;">{{ leaveRequest.days_requested }}</span></div></el-col>
          <el-col :xs="12" :md="4"><div class="kv"><span class="k">시간</span><span class="v">{{ leaveRequest.session === 'full' ? '전일' : leaveRequest.session === 'morning' ? '오전' : leaveRequest.session === 'afternoon' ? '오후' : leaveRequest.session }}</span></div></el-col>
          <el-col :xs="24" :md="4"><div class="kv"><span class="k">사유</span><span class="v">{{ leaveRequest.reason || '-' }}</span></div></el-col>
        </el-row>
        <!-- 증빙 첨부 — 무급휴가·훈련휴가 등 증빙 필수 유형은 여기서 서류를 확인하고 승인한다. -->
        <el-row v-if="leaveRequest.attachments && leaveRequest.attachments.length" :gutter="12" style="margin-top:8px;">
          <el-col :span="24">
            <div class="kv kv-att">
              <span class="k">증빙</span>
              <span class="v att-links">
                <a
                  v-for="att in leaveRequest.attachments"
                  :key="att.id"
                  :href="att.file_url || att.file"
                  target="_blank"
                  rel="noopener"
                  class="att-link"
                >📎 {{ att.original_name || '첨부파일' }}</a>
              </span>
            </div>
          </el-col>
        </el-row>
      </el-card>

      <!-- 신청자 휴가 잔량 -->
      <h4 style="margin: 8px 0;">📊 신청자 휴가 잔량</h4>
      <el-table v-if="Array.isArray(statistics) && statistics.length" :data="statistics" class="responsive-table" size="small" style="margin-bottom:12px;">
        <el-table-column prop="leave_type_name" label="휴가 구분" width="100">
          <template #default="{ row }">
            {{ row.leave_type_name }}
            <el-tag v-if="row.leave_type_name === '생일반차'" type="warning" effect="light" size="small">🎂</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="total_days" label="총 일수" width="80" />
        <el-table-column prop="remaining_days" label="남은 일수" width="80">
          <template #default="{ row }">
            <span :style="{ color: row.remaining_days <= 0 ? '#f56c6c' : '#67c23a', fontWeight: 700 }">{{ row.remaining_days }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="used_days" label="사용" width="80" />
        <!-- 결재 대기분은 '남은 일수'에 아직 반영되지 않는다. 승인자가 초과를
             사전에 인지하도록 별도 노출한다. -->
        <el-table-column prop="pending_days" label="결재 대기" width="90">
          <template #default="{ row }">
            <span :style="{ color: row.pending_days > 0 ? '#e6a23c' : 'inherit', fontWeight: row.pending_days > 0 ? 700 : 400 }">
              {{ row.pending_days || 0 }}
            </span>
            <el-tag
              v-if="row.pending_days > 0 && (row.remaining_days - row.pending_days) < 0"
              type="danger" effect="light" size="small" style="margin-left:4px;"
            >초과 예상</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="exceeded_days" label="초과" width="80" />
        <el-table-column prop="valid_from" label="사용 시작일" width="110" />
        <el-table-column prop="valid_to" label="사용 종료일" width="110" />
      </el-table>

      <!-- 과거 사용 내역 (펼치기) -->
      <el-collapse v-model="usageHistoryOpen" style="margin-bottom:12px;">
        <el-collapse-item name="usage">
          <template #title>
            <span style="font-weight:600;">📅 과거 사용 내역 ({{ leaveDetails.length }}건)</span>
          </template>
          <el-table v-if="leaveDetails.length" :data="leaveDetails" class="responsive-table" size="small">
            <el-table-column prop="start_date" label="시작일" />
            <el-table-column prop="end_date" label="종료일" />
            <el-table-column label="휴가 유형">
              <template #default="{ row }">
                {{ row.leave_type_name }}
                <el-tag v-if="row.leave_type_name === '생일반차'" type="warning" effect="light" size="small">🎂</el-tag>
              </template>
            </el-table-column>
            <el-table-column label="시간">
              <template #default="{ row }">
                {{ row.session==="full"?"전일": row.session === "morning" ? "오전" : row.session === "afternoon" ? "오후" : row.session }}
              </template>
            </el-table-column>
            <el-table-column prop="reason" label="사유" />
          </el-table>
          <el-empty v-else description="과거 사용 내역 없음" :image-size="60" />
        </el-collapse-item>
      </el-collapse>

      <template #footer>
        <div class="approval-actions">
          <el-button @click="detailsDialogVisible = false" class="btn-cancel">닫기</el-button>
          <el-button type="danger" plain @click="openRejectDialog" class="btn-action">반려</el-button>
          <el-button type="success" @click="approveLeave" class="btn-action">승인</el-button>
        </div>
      </template>
    </el-dialog>

    <!-- 반려 다이얼로그 -->
    <el-dialog v-model="rejectDialogVisible" title="반려 사유 입력" width="80%">
      <el-input type="textarea" v-model="rejectionReason" placeholder="반려 사유를 입력하세요." />
      <template #footer>
        <el-button @click="rejectDialogVisible = false" class="full-width">취소</el-button>
        <el-button type="danger" @click="confirmReject" class="full-width">반려</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from "vue";
import { useRoute } from "vue-router";
import axiosInstance from "@/api/axios"; // Axios 인스턴스 가져오기

const route = useRoute();

const pendingLeaves = ref([]);
//const selectedLeave = ref({});
const statistics = ref([]);
const usageHistoryOpen = ref([]); // 과거 사용 내역 펼치기 상태 (기본 닫힘)

// 모달 너비 반응형 (모바일 95%, 데스크탑 720px)
const dialogWidth = computed(() => (typeof window !== "undefined" && window.innerWidth < 768 ? "95%" : "720px"));
const leaveDetails = ref([]);
const rejectDialogVisible = ref(false);
const rejectionReason = ref("");
const detailsDialogVisible = ref(false);
const selectedLeaveId = ref(null);
const leaveRequest = ref({});

const columnDefs = ref([
  { field: "employee", headerName: "신청자", sortable: true, filter: true, resizable: true },
  { field: "start_date", headerName: "시작일", sortable: true, filter: true, resizable: true },
  { field: "days_requested", headerName: "휴가일수", sortable: true, filter: true, resizable: true },
  { field: "end_date", headerName: "종료일", sortable: true, filter: true, resizable: true },
  { field: "leave_type_name", headerName: "휴가 유형", sortable: true, filter: true, resizable: true },
  { 
    field: "session", 
    headerName: "SESSION", 
    sortable: true, 
    filter: true, 
    resizable: true,
    valueFormatter: params => {
      return params.session==="full"?"전일": params.value === "morning" ? "오전" : params.value === "afternoon" ? "오후" : params.value;

    }
  },
]);

const fetchPendingLeaves = async () => {
  try {
    const response = await axiosInstance.get(`leaves/pending/`);
    pendingLeaves.value = response.data;
  } catch (error) {
    console.error("승인 대기 리스트 조회 실패:", error);
    alert("승인 대기 리스트를 가져오는 중 오류가 발생했습니다.");
  }
};

const openLeaveDetails = async (row) => {
  try {
    const response = await axiosInstance.get(`leaves/details/${row.id}/`);
    // 신규: statistics 배열 (LeaveQuota 전체 + 생일반차). 없으면 leave_quota(단일) 호환
    statistics.value = response.data.statistics || (response.data.leave_quota ? [response.data.leave_quota] : []);
    leaveDetails.value = response.data.leave_details;
    selectedLeaveId.value = row.id;
    leaveRequest.value = response.data.leave_request;
    detailsDialogVisible.value = true;
  } catch (error) {
    console.error("휴가 상세 정보 조회 실패:", error);
    alert("휴가 상세 정보를 가져오는 중 오류가 발생했습니다.");
  }
};

// ag-grid 행 클릭 핸들러
const fetchLeaveDetails = (event) => openLeaveDetails(event.data);

const approveLeave = async () => {
  try {
    await axiosInstance.post(`leaves/${selectedLeaveId.value}/approve/`);
    alert("휴가 요청이 승인되었습니다.");
    fetchPendingLeaves();
    detailsDialogVisible.value = false;
  } catch (error) {
    console.error("휴가 승인 실패:", error);
    alert("휴가 승인 중 오류가 발생했습니다.");
  }
};

const openRejectDialog = () => {
  rejectDialogVisible.value = true;
};

const confirmReject = async () => {
  if (!rejectionReason.value) {
    alert("반려 사유를 입력하세요.");
    return;
  }

  try {
    await axiosInstance.post(`leaves/${selectedLeaveId.value}/reject/`, {
      rejection_reason: rejectionReason.value,
    });
    alert("휴가 요청이 반려되었습니다.");
    rejectDialogVisible.value = false;
    fetchPendingLeaves();
    detailsDialogVisible.value = false;
  } catch (error) {
    console.error("휴가 반려 실패:", error);
    alert("휴가 반려 중 오류가 발생했습니다.");
  }
};

onMounted(async () => {
  await fetchPendingLeaves();

  // 결재함에서 ?leave_id= 로 진입 시 해당 건 결재 다이얼로그 자동 오픈
  const leaveId = Number(route.query.leave_id);
  if (leaveId) {
    const row = pendingLeaves.value.find((l) => l.id === leaveId);
    if (row) {
      openLeaveDetails(row);
    }
  }
});
</script>

<style scoped>
.container {
  padding: 10px;
}

.grid-container {
  width: 100%;
  height: 400px;
}

.responsive-dialog {
  width: 90%;
  max-width: 600px;
}

.responsive-table {
  width: 100%;
  overflow-x: auto;
}

.button-container {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 20px;
}

.full-width {
  width: 100%;
}

.kv { display: flex; flex-direction: column; gap: 2px; padding: 4px 0; }
.kv .k { font-size: 12px; color: #909399; }
.kv .v { font-size: 14px; color: #303133; }

/* 결재 모달 — 데스크탑 우측 정렬, 모바일 풀와이드 */
.approval-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}
.approval-actions .btn-cancel,
.approval-actions .btn-action {
  min-width: 88px;
}
@media (max-width: 768px) {
  .approval-actions {
    flex-direction: column-reverse;  /* 모바일: 승인이 가장 위 (탭하기 쉽게) */
    gap: 8px;
  }
  .approval-actions .btn-cancel,
  .approval-actions .btn-action {
    width: 100%;
    min-width: 0;
    padding: 12px 16px;  /* 모바일 탭 영역 확대 */
    font-size: 15px;
  }
}
/* 모달 헤더가 너무 두꺼우면 컴팩트하게 */
.approval-dialog :deep(.el-dialog__body) {
  padding: 16px 20px;
}
.approval-dialog :deep(.el-dialog__footer) {
  padding: 12px 20px 16px;
  border-top: 1px solid #f0f0f0;
}
.kv-att {
  align-items: flex-start;
}
.att-links {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.att-link {
  color: #409eff;
  text-decoration: none;
  font-size: 13px;
  word-break: break-all;
}
.att-link:hover {
  text-decoration: underline;
}

</style>
